/**
 * ServerStatusBadge — Tag AntD coloré + icône Lucide par status.
 *
 * @spec specs/app/admin/dedicated-servers.md §6.1
 */

import { Tag, Tooltip } from 'antd';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Pause,
  Trash2,
  Zap,
} from 'lucide-react';
import type { FC, ReactNode } from 'react';
import {
  SERVER_STATUS_DESCRIPTIONS,
  SERVER_STATUS_LABELS,
  type ServerStatus,
} from '@/types/dedicatedServer';

interface ServerStatusBadgeProps {
  status: ServerStatus;
  size?: 'small' | 'default' | 'large';
}

const STATUS_STYLE: Record<
  ServerStatus,
  { color: string; icon: ReactNode; spin?: boolean }
> = {
  provisioning: { color: 'blue', icon: <Zap className="h-3.5 w-3.5" /> },
  installing: {
    color: 'cyan',
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
    spin: true,
  },
  active: { color: 'green', icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  failed: { color: 'red', icon: <AlertCircle className="h-3.5 w-3.5" /> },
  suspended: { color: 'default', icon: <Pause className="h-3.5 w-3.5" /> },
  destroyed: { color: 'volcano', icon: <Trash2 className="h-3.5 w-3.5" /> },
};

const SIZE_PADDING: Record<NonNullable<ServerStatusBadgeProps['size']>, string> = {
  small: 'px-1.5 py-0.5 text-[11px]',
  default: 'px-2 py-1 text-xs',
  large: 'px-3 py-1.5 text-sm',
};

const ServerStatusBadge: FC<ServerStatusBadgeProps> = ({ status, size = 'default' }) => {
  const style = STATUS_STYLE[status];
  return (
    <Tooltip title={SERVER_STATUS_DESCRIPTIONS[status]}>
      <Tag
        color={style.color}
        className={`inline-flex items-center gap-1.5 font-medium ${SIZE_PADDING[size]}`}
      >
        {style.icon}
        <span>{SERVER_STATUS_LABELS[status]}</span>
      </Tag>
    </Tooltip>
  );
};

export default ServerStatusBadge;
